import {Icon, Profile, Text, Tooltip, Modal, Button} from '@/ui-kits';
import {cn, formatName} from '@/utils/common_functions';
import React, {useEffect, useLayoutEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {getChatMessagesRequest, sendChatMessageRequest} from '@/services/redux/slices';
import {chatMessagesSelector, chatLoadingSelector, accountDeletionStatusSelector} from '@/services/redux/selectors';
import {Message} from '@/ui-kits/Message';
import {Info, Loader2, X} from 'lucide-react';
import {useFormSubmit, useRole} from '@/hooks';
import {BULLET, ChatMessageType, ConsultationStatus, PatientAccountTicketRequestStatus, ProfanityMessage} from '@/constants';
import {BulletTextInput} from './BulletTextInput';
import { hasBadWords } from '@/utils/profanity';
import { ScheduleMeetModal } from './ScheduleMeetModal';
import { Video } from 'lucide-react';
import { getProviders } from '@/services/api';

interface ChatPanelProps {
  reciever_profile_photo: string;
  reciever_name: string;
  case_id: number;
  case_status: ConsultationStatus;
  onClose: () => void;
  className?: string;
  specialization?: string;
  chatPanelRef?: any;
  style?: React.CSSProperties;
  open: boolean;
}

const formatMessageDate = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();

  const isToday = date.toDateString() === today.toDateString();

  if (isToday) return 'Today';

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

export const ChatPanel = (props: ChatPanelProps) => {
  const {reciever_profile_photo, reciever_name, case_id, onClose, className, case_status, specialization, chatPanelRef, style, open} = props;
  // ==========================
  // hooks
  // ==========================
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {isPatient} = useRole();

  // ==========================
  // selectors
  // ==========================
  const chatMessages = useSelector(chatMessagesSelector(case_id));
  const chatLoading = useSelector(chatLoadingSelector(case_id));
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const [isDocumentMessage, setIsDocumentMessage] = useState<boolean>(false);
  const [isMeetModalOpen, setIsMeetModalOpen] = useState<boolean>(false);
  const [noProviderModalOpen, setNoProviderModalOpen] = useState<boolean>(false);
  const [connectedProviders, setConnectedProviders] = useState<any[]>([]);
  const [checkingProvider, setCheckingProvider] = useState<boolean>(false);

  // ==========================
  // state and refs
  // ==========================
  const [message, setMessage] = useState<string>('');
  const messagesEndRef = React.useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string>('');

  // ==========================
  // functions
  // ==========================
  const normalizeBullets = (raw: string) => {
    const items = raw.split('\n');
    const replaced = items.map(line => line.replace(BULLET, '').trim());
    const filtered = replaced.filter(item => item.length > 0);
    return filtered.join(` ${BULLET.trim()} `);
  };

  const handleMessageChange = (value: string) => {
    setMessage(value);
  };

  const handleSendMessage = () => {
    if (!message) return;
    if (!open) return;

    // check if the message contains any bads words
    if (hasBadWords(message)){
      setError(ProfanityMessage)
      setMessage("")
      return;
    }
    else{
      setError('')
    }

    let message_type = ChatMessageType.Text;
    let formatted_message: any = message;

    if (isDocumentMessage && !isPatient) {
      message_type = ChatMessageType.DocumentRequest;
      formatted_message = normalizeBullets(message);
    }

    dispatch(sendChatMessageRequest({id: case_id, message: formatted_message, type: message_type}));
    setMessage('');
    // reset the document message after it has been sent
    setIsDocumentMessage(false);
  };

  const toggleDocumentRequest = () => {
    if (isPatient) return;
    setIsDocumentMessage(p => !p);
  };

  // ==========================
  // side effect
  // ==========================
  useFormSubmit(() => {
    if (isDocumentMessage) return;
    handleSendMessage();
  });
  // get chat messages
  useEffect(() => {
    if (!open) return;
    dispatch(getChatMessagesRequest({id: case_id}));
  }, [case_id, dispatch, open]);

  // scroll to bottom of the chat when new messages are added
  useLayoutEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({behavior: 'smooth'});
    }
  }, [chatMessages]);

  useEffect(() => {
    setMessage('');
    setError('')
  }, [isDocumentMessage]);

  const handleVideoIconClick = async () => {
    setCheckingProvider(true);
    try {
      const res = await getProviders();
      const providers = res?.data?.data || [];
      const connected = providers.filter((p: any) => p.is_connected);
      if (connected.length > 0) {
        setConnectedProviders(connected);
        setIsMeetModalOpen(true);
      } else {
        setNoProviderModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to check provider status:', err);
      setNoProviderModalOpen(true);
    } finally {
      setCheckingProvider(false);
    }
  };

  return (
    <div
      style={{display: open ? 'flex' : 'none', ...style}}
      ref={chatPanelRef}
      className={cn(
        'fixed bottom-4 p-2 gap-4 z-[999] right-1 sm:right-10 lg:right-20 w-[95%] max-w-[300px] h-[400px] md:max-w-[400px] md:h-[500px] xl:max-w-[500px] xl:h-[600px] bg-gradient-to-br from-ui_blue to-ui_blue_light rounded-2xl flex-col',
        className,
      )}>
      {/* Header */}
      <div className="flex items-center gap-1.5 md:gap-2 px-2 pt-2 md:pt-4">
        <Profile username={reciever_name} profile_url={reciever_profile_photo} className="!size-10 md:!size-14" />
        <div className="flex flex-col grow">
          <Text variant="title" className="text-white !font-FigtreeBold text-sm md:text-base">
            {isPatient && 'Dr. '}
            {formatName(reciever_name)}
          </Text>
          <Text variant="small" className="text-white !font-FigtreeSemiBold text-xs md:text-sm">
            {isPatient && specialization ? (
              <>
                <span className="text-ui_bg_light_blue !font-FigtreeLight">Specialization :</span> {specialization}
              </>
            ) : (
              <>
                <span className="text-ui_bg_light_blue !font-FigtreeLight">Case ID:</span> #{case_id}
              </>
            )}
          </Text>
        </div>
        <button onClick={onClose} className="self-start -mt-1 md:-mt-2">
          <Icon type="close_white" className="size-5 md:size-6" />
        </button>
      </div>

      {/* chat sections */}
      <div className="grow bg-white min-h-0 rounded-lg flex flex-col">
        <div className="flex flex-col gap-2 grow p-2 min-h-0 overflow-y-auto">
          {chatLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="size-6 animate-spin" />
            </div>
          ) : (
            chatMessages.map((msg, index) => {
              const currentDate = formatMessageDate(msg.created_at);
              const prevDate = index > 0 ? formatMessageDate(chatMessages[index - 1].created_at) : null;

              const shouldShowSeparator = currentDate !== prevDate;

              return (
                <React.Fragment key={msg.id}>
                  {shouldShowSeparator && <MessageDateSeparator label={currentDate} />}
                  <Message {...msg} consultation_id={case_id} other_user_name={reciever_name} />
                </React.Fragment>
              );
            })
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* input */}
        {[ConsultationStatus.Completed, ConsultationStatus.ReportReady].includes(case_status) ||
        (isPatient && accountDeletionStatus === PatientAccountTicketRequestStatus.Approved) ? (
          <div className="flex m-2 items-center gap-2 rounded-xl border-ui_input_border p-2 border bg-[#F2F2F2]">
            <Text variant="small" className="text-ui_blue !font-FigtreeMeduim">
              {isPatient && accountDeletionStatus === PatientAccountTicketRequestStatus.Approved
                ? 'Chat disabled. No further messages can be sent.'
                : 'Chat closed. No further messages can be sent.'}
            </Text>
          </div>
        ) : (
          <div className="flex p-2 gap-2">
            {!isPatient && (
              <Tooltip tooltipClassName="!translate-x-[-18px]" className="flex" textClassName="!text-xs" message="Request Additional Reports">
                <button
                  onClick={toggleDocumentRequest}
                  className={cn(
                    'border flex justify-center items-center size-10 rounded-full relative',
                    isDocumentMessage ? 'bg-gradient-to-tr from-ui_blue to-ui_blue_light self-end' : 'bg-[#ECF8FE] self-center',
                  )}>
                  {isDocumentMessage && (
                    <div className="bg-ui_sky_blue flex justify-center items-start border top-0 right-0 translate-x-1 -translate-y-1 rounded-full absolute size-4">
                      <X className="size-3 text-ui_blue" />
                    </div>
                  )}
                  <Icon type={isDocumentMessage ? 'request_document_white' : 'request_document'} className="size-5" />
                </button>
              </Tooltip>
            )}
            {!isPatient && (
              <Tooltip tooltipClassName="!translate-x-[-18px]" className="flex" textClassName="!text-xs" message="Schedule Video Meeting">
                <button
                  onClick={handleVideoIconClick}
                  disabled={checkingProvider}
                  className="border flex justify-center items-center size-10 rounded-full relative bg-[#ECF8FE] self-center disabled:opacity-50">
                  {checkingProvider ? <Loader2 className="size-5 text-ui_blue animate-spin" /> : <Video className="size-5 text-ui_blue" />}
                </button>
              </Tooltip>
            )}
            <div className="flex flex-col w-full">
              {error && (
                <span className='flex gap-1 items-start justify-end mr-1 mb-1'>
                  <Info className='rotate-180 size-3.5 mt-[5px] text-rejection'/>
                  <Text variant="small" className="text-rejection self-end">
                    {error}
                  </Text>
                </span>
              )}
              <div
                className={cn(
                  'flex items-center gap-2 rounded-xl px-2 border grow',
                  isDocumentMessage ? 'border-ui_blue bg-ui_sky_blue' : 'border-ui_input_border',
                  error && 'border-rejection',
                )}>
                {isDocumentMessage && !isPatient ? (
                  <BulletTextInput value={message} placeholder="Enter document required" onChange={handleMessageChange} />
                ) : (
                  <input
                    className={cn(
                      'grow outline-none text-ui_input_border_dark placeholder:text-ui_input_border_dark bg-transparent',
                      isPatient && 'min-h-[50px] p-1',
                    )}
                    placeholder="Your Message"
                    value={message}
                    onChange={e => handleMessageChange(e.target.value)}
                    disabled={chatLoading}
                  />
                )}

                <button onClick={handleSendMessage} disabled={!message || chatLoading} className={isDocumentMessage ? 'self-end' : 'self-center'}>
                  <Icon type="send" className="size-6" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <ScheduleMeetModal isOpen={isMeetModalOpen} onClose={() => setIsMeetModalOpen(false)} case_id={case_id} connectedProviders={connectedProviders} />
      <Modal open={noProviderModalOpen} onClose={() => setNoProviderModalOpen(false)} className="rounded-2xl max-w-md w-full p-6 bg-white">
        <div className="flex flex-col items-center text-center gap-4">
          <div className="p-3 bg-amber-50 rounded-full mb-2">
            <Info className="size-10 text-amber-500" />
          </div>
          <div>
            <Text variant="title" className="text-deep_blue !font-FigtreeBold">
              No Video Provider Connected
            </Text>
            <Text variant="body" className="text-ui_placeholder mt-3 text-sm">
              To schedule a video consultation, please connect a video provider (Google Meet or Microsoft Teams) in your Settings.
            </Text>
          </div>
          <div className="flex w-full gap-3 mt-4">
            <Button
              title="Cancel"
              variant="secondary"
              onClick={() => setNoProviderModalOpen(false)}
              className="flex-1"
            />
            <Button
              title="Go to Settings"
              variant="primary"
              className="flex-1"
              onClick={() => {
                setNoProviderModalOpen(false);
                navigate('/settings');
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

const MessageDateSeparator = ({label}: {label: string}) => {
  return (
    <div className="flex justify-center my-2">
      <span className="px-3 py-1 text-xs rounded-sm text-ui_blue bg-ui_sky_blue">{label}</span>
    </div>
  );
};
